import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { api, ApiError } from '../../lib/api';
import type { CaptchaFields } from '../../lib/auth';
import { Captcha } from '../Captcha';
import { Field, Modal, Select, TextInput } from '../ui';

/**
 * A guest asking the hotel about their data, from the hotel's public pages.
 *
 * Submitting changes nothing and reveals nothing: it records a request the
 * hotel then verifies and answers. The guest gets a reference, not a
 * confirmation of whether the hotel knows them.
 */
export function GuestDataRequestLink({ token }: { token: string }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" className="linklike small" onClick={() => setOpen(true)}>
        Your data and privacy
      </button>
      {open ? <GuestDataRequestForm token={token} onClose={() => setOpen(false)} /> : null}
    </>
  );
}

function GuestDataRequestForm({ token, onClose }: { token: string; onClose: () => void }) {
  const [requestType, setRequestType] = useState('ACCESS');
  const [name, setName] = useState('');
  const [contact, setContact] = useState('');
  const [details, setDetails] = useState('');
  const [captcha, setCaptcha] = useState<CaptchaFields>({});
  const [captchaToken, setCaptchaToken] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [reference, setReference] = useState<string | null>(null);

  const send = useMutation({
    mutationFn: () =>
      api.post<{ reference: string }>(`/api/public/menu/${token}/data-requests`, {
        requestType,
        name,
        contact,
        details,
        ...captcha,
      }),
    onSuccess: (result) => setReference(result.reference),
    onError: (caught) => {
      setError(caught instanceof ApiError ? caught.message : 'Could not send that. Try again.');
      setCaptchaToken((n) => n + 1);
    },
  });

  if (reference) {
    return (
      <Modal title="Request sent" size="narrow" onClose={onClose}>
        <p className="small">
          The hotel has your request, reference <b className="mono">{reference}</b>. They will check it is
          you before acting on it, and reply to the contact you gave.
        </p>
      </Modal>
    );
  }

  return (
    <Modal
      title="Your data and privacy"
      size="narrow"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn primary"
            disabled={send.isPending || !name.trim() || contact.trim().length < 5 || details.trim().length < 5}
            onClick={() => {
              setError(null);
              send.mutate();
            }}
          >
            Send to the hotel
          </button>
        </>
      }
    >
      <div className="col gap-14">
        {error ? <div className="form-error">{error}</div> : null}
        <Select label="What would you like?" value={requestType} onChange={(e) => setRequestType(e.target.value)}>
          <option value="ACCESS">A copy of the data you hold about me</option>
          <option value="CORRECTION">Correct my details</option>
          <option value="ERASURE">Erase my details</option>
          <option value="CONSENT_WITHDRAWAL">Withdraw a consent I gave</option>
          <option value="GRIEVANCE">Raise a complaint</option>
        </Select>
        <TextInput label="Your name" value={name} onChange={(e) => setName(e.target.value)} />
        <TextInput label="Email or phone to reply to" value={contact} onChange={(e) => setContact(e.target.value)} />
        <Field label="Details" hint="Dates of your stay or booking help the hotel find you">
          <textarea className="input" rows={3} value={details} onChange={(e) => setDetails(e.target.value)} />
        </Field>
        <Captcha value={captcha} onChange={setCaptcha} reloadToken={captchaToken} />
      </div>
    </Modal>
  );
}
